/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.getPublicAmazonProductVariants

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.getPublicAmazonProductVariants.internal.GetPublicAmazonProductVariantsUseCase
import features.getPublicAmazonProductVariants.internal.GetPublicAmazonProductVariantsUseCase.Params
import features.getPublicAmazonProductVariants.models.PublicRewardResponseModel


typealias GetPublicAmazonProductVariantsState = CFlow<GetPublicAmazonProductVariantsUseCaseResult?>

typealias GetPublicAmazonProductVariantsUseCaseResult = Result<PublicRewardResponseModel>

fun GetPublicAmazonProductVariantsUseCaseResult.serializeGetPublicAmazonProductVariantsApiResult(): SerializedResult<String> {
    return this.toJsonResult<PublicRewardResponseModel>()
}

/**
 * Serializes [GetPublicAmazonProductVariantsState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun GetPublicAmazonProductVariantsState.serializeGetPublicAmazonProductVariantsState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<PublicRewardResponseModel>()
    }.asCFlow()
}

/**
 * Get a public reward with external product variants by external product id [Api] class
 */
class GetPublicAmazonProductVariantsApi : Api() {

    val getPublicAmazonProductVariantsState: GetPublicAmazonProductVariantsState
        get() = _getPublicAmazonProductVariantsState.asCFlow()

    companion object {
        private val _getPublicAmazonProductVariantsState: MutableStateFlow<GetPublicAmazonProductVariantsUseCaseResult> = MutableStateFlow(Idle())
    }

    private val getPublicAmazonProductVariantsUseCase = GetPublicAmazonProductVariantsUseCase()

    /**
     * Get a public reward with external product variants by external product id
     */
    suspend fun getPublicAmazonProductVariants(
        overrideHeaders: Map<String, String>? = null,
        rewardSlug: String,
        externalProductId: String
    ): Result<PublicRewardResponseModel> {
        /* set state to Loading as soon as function is called */
        _getPublicAmazonProductVariantsState.value = Loading()
        val result = this.getPublicAmazonProductVariantsUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                rewardSlug = rewardSlug,
                externalProductId = externalProductId
            )
        ) { result ->
            _getPublicAmazonProductVariantsState.value = result
            return@getPublicAmazonProductVariantsUseCase result
        }
        return result
    }

}
